import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { IconButton } from '../components/IconButton';
import { IconBack, IconDownload } from '../components/icons';
import { Modal } from '../components/Modal';
import { useAuth } from '../auth/useAuth';
import { labelFor, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { downloadFile } from '../utils/download';
import { formatMoney } from '../utils/format';

interface ApprovalLevelDef {
  level: number;
  name: string;
  min_amount: number;
  role: string;
}

interface Approval {
  id: number;
  level: number;
  decision: 'approved' | 'rejected';
  comment: string | null;
  decided_at: string;
  approver: { id: number; full_name: string } | null;
}

interface Cheque {
  id: number;
  cheque_number: string;
  status: string;
  required_signatures: number;
  signatures_collected: number;
}

interface BatchLine {
  id: number;
  status: string;
  batch: { id: number; batch_number: string; status: string };
}

interface DocumentRow {
  id: number;
  original_name: string;
  url: string;
}

interface Reversal {
  id: number;
  requested_by: { id: number; full_name: string } | null;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  decided_by: { id: number; full_name: string } | null;
  decision_comment: string | null;
}

interface BenefitClaimRecord {
  id: number;
  reference: string;
  claim_type: string;
  member: { id: number; user: { first_name: string; last_name: string } | null };
  beneficiary: { id: number; full_name: string; relationship: string } | null;
  claimed_amount: string | null;
  calculated_entitlement: string | null;
  entitlement_calculation_note: string | null;
  eligibility_conditions_met: boolean;
  eligibility_flags: { condition: string; detail: string }[] | null;
  approved_amount: string | null;
  override_amount: boolean;
  override_reason: string | null;
  payment_method: 'bank_transfer' | 'cheque' | null;
  status: string;
  assessed_by: { id: number; full_name: string } | null;
  approval_levels_required: ApprovalLevelDef[] | null;
  current_approval_level: number;
  suspense_status: string | null;
  suspense_reason: string | null;
  reversed_at: string | null;
  reversal_reason: string | null;
  reissued_count: number;
  approvals: Approval[];
  active_batch_line: BatchLine | null;
  active_cheque: Cheque | null;
  documents: DocumentRow[];
  reversals: Reversal[];
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  pending_approval: 'Pending Approval',
  rejected: 'Rejected',
  approved: 'Approved',
  payment_generated: 'Payment Generated',
  disbursed: 'Disbursed',
  returned_to_suspense: 'Returned to Suspense',
  reversed: 'Reversed',
};

export function BenefitClaimDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can, hasRole } = useAuth();
  const { settings, lists } = useConfig();
  const confirmAction = useConfirm();
  const { showError, showSuccess } = useToast();

  const [claim, setClaim] = useState<BenefitClaimRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const [decidingLevel, setDecidingLevel] = useState<ApprovalLevelDef | null>(null);
  const [decisionForm, setDecisionForm] = useState({ decision: 'approved' as 'approved' | 'rejected', comment: '', approved_amount: '', override_reason: '' });

  const [requestingReversal, setRequestingReversal] = useState(false);
  const [reversalReason, setReversalReason] = useState('');

  const [returningToSuspense, setReturningToSuspense] = useState(false);
  const [suspenseReason, setSuspenseReason] = useState('');

  const [decidingReversal, setDecidingReversal] = useState<Reversal | null>(null);
  const [reversalDecisionForm, setReversalDecisionForm] = useState({ decision: 'approved' as 'approved' | 'rejected', decision_comment: '' });

  const canCreate = can('memberships.benefits.create');
  const canAssess = can('memberships.benefits.assess');
  const canDisburse = can('memberships.benefits.disburse');
  const canRequestReversal = can('memberships.benefits.reverse.request');
  const canDecideReversal = can('memberships.benefits.reverse.decide');

  const fmtMoney = (n: string | null) => (n === null ? '—' : formatMoney(Number(n), settings.currency));

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get<BenefitClaimRecord>(`/benefit-claims/${id}`);
      setClaim(res.data);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load claim.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const currentLevelDef = claim?.approval_levels_required?.find((l) => l.level === claim.current_approval_level) ?? null;
  const canActOnCurrentLevel = Boolean(currentLevelDef && (hasRole(currentLevelDef.role) || hasRole('system_admin')));

  const submitClaim = async () => {
    if (!claim) return;
    if (!(await confirmAction('Submit this claim? Entitlement will be calculated and the approval chain locked in.', { confirmLabel: 'Submit' }))) return;
    try {
      await api.post(`/benefit-claims/${claim.id}/submit`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Submit failed.');
    }
  };

  const assessClaim = async () => {
    if (!claim) return;
    try {
      await api.post(`/benefit-claims/${claim.id}/assess`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Assess failed.');
    }
  };

  const openDecide = (levelDef: ApprovalLevelDef) => {
    if (!claim) return;
    setDecisionForm({ decision: 'approved', comment: '', approved_amount: claim.calculated_entitlement ?? '', override_reason: '' });
    setErrors({});
    setDecidingLevel(levelDef);
  };

  const submitDecision = async (e: FormEvent) => {
    e.preventDefault();
    if (!claim) return;
    try {
      await api.post(`/benefit-claims/${claim.id}/decide`, {
        decision: decisionForm.decision,
        comment: decisionForm.comment || undefined,
        approved_amount: decisionForm.decision === 'approved' ? Number(decisionForm.approved_amount) : undefined,
        override_reason: decisionForm.override_reason || undefined,
      });
      setDecidingLevel(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] });
    }
  };

  const submitReversalRequest = async (e: FormEvent) => {
    e.preventDefault();
    if (!claim) return;
    try {
      await api.post(`/benefit-claims/${claim.id}/reversals`, { reason: reversalReason });
      setRequestingReversal(false);
      setReversalReason('');
      showSuccess('Reversal request submitted.');
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Request failed.');
    }
  };

  const submitReversalDecision = async (e: FormEvent) => {
    e.preventDefault();
    if (!decidingReversal) return;
    try {
      await api.post(`/benefit-claim-reversals/${decidingReversal.id}/decide`, reversalDecisionForm);
      setDecidingReversal(null);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Save failed.');
    }
  };

  const submitReturnToSuspense = async (e: FormEvent) => {
    e.preventDefault();
    if (!claim) return;
    try {
      await api.post(`/benefit-claims/${claim.id}/mark-returned`, { suspense_reason: suspenseReason });
      setReturningToSuspense(false);
      setSuspenseReason('');
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Failed.');
    }
  };

  const reissuePayment = async () => {
    if (!claim) return;
    if (!(await confirmAction('Reissue payment for this claim? Make sure the underlying issue has been corrected first.', { confirmLabel: 'Reissue' }))) return;
    try {
      await api.post(`/benefit-claims/${claim.id}/reissue`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Reissue failed.');
    }
  };

  const removeClaim = async () => {
    if (!claim) return;
    if (!(await confirmAction('Delete this draft claim?'))) return;
    try {
      await api.delete(`/benefit-claims/${claim.id}`);
      navigate('/memberships/benefits/claims');
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const downloadClaimPdf = () => {
    if (!claim) return;
    downloadFile(api, `/benefit-claims/${claim.id}/pdf`, `${claim.reference}.pdf`);
  };

  const uploadDocument = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !claim) return;
    const data = new FormData();
    data.append('file', file);
    try {
      await api.post(`/benefit-claims/${claim.id}/documents`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Upload failed.');
    }
  };

  const removeDocument = async (documentId: number) => {
    if (!claim) return;
    if (!(await confirmAction('Remove this document?'))) return;
    await api.delete(`/benefit-claims/${claim.id}/documents/${documentId}`);
    load();
  };

  if (loading) return null;
  if (error || !claim) {
    return (
      <>
        <header className="page-header">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/memberships/benefits/claims')} />
        </header>
        <div className="card">
          <p className="error">{error ?? 'Claim not found.'}</p>
        </div>
      </>
    );
  }

  const pendingReversal = claim.reversals.find((r) => r.status === 'pending') ?? null;

  return (
    <>
      <header className="page-header">
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back to list" onClick={() => navigate('/memberships/benefits/claims')} />
          <h1>
            {claim.reference} — {claim.member.user ? `${claim.member.user.first_name} ${claim.member.user.last_name}` : `Member #${claim.member.id}`}
          </h1>
        </div>
        <div className="page-header-actions">
          <span className="badge badge-neutral">{STATUS_LABELS[claim.status] ?? claim.status}</span>
          <IconButton icon={IconDownload} label="Download PDF" onClick={downloadClaimPdf} />
          {canCreate && claim.status === 'draft' && (
            <button type="button" className="btn btn-primary" onClick={submitClaim}>
              Submit
            </button>
          )}
          {canAssess && claim.status === 'submitted' && (
            <button type="button" className="btn btn-primary" onClick={assessClaim}>
              Assess
            </button>
          )}
          {canDisburse && claim.status === 'disbursed' && (
            <button type="button" className="btn btn-ghost" onClick={() => setReturningToSuspense(true)}>
              Mark Returned to Suspense
            </button>
          )}
          {canDisburse && claim.status === 'returned_to_suspense' && (
            <button type="button" className="btn btn-primary" onClick={reissuePayment}>
              Reissue Payment
            </button>
          )}
          {canRequestReversal && !pendingReversal && ['disbursed', 'returned_to_suspense'].includes(claim.status) && (
            <button type="button" className="btn btn-ghost" onClick={() => setRequestingReversal(true)}>
              Request Reversal
            </button>
          )}
          {canCreate && claim.status === 'draft' && (
            <button type="button" className="btn btn-danger-ghost" onClick={removeClaim}>
              Delete
            </button>
          )}
        </div>
      </header>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="form-grid">
          <div>
            <strong>Claim Type</strong>
            <p>{labelFor(lists, 'benefit_claim_type', claim.claim_type)}</p>
          </div>
          <div>
            <strong>Claimed Amount</strong>
            <p>{fmtMoney(claim.claimed_amount)}</p>
          </div>
          <div>
            <strong>Calculated Entitlement</strong>
            <p>{fmtMoney(claim.calculated_entitlement)}</p>
          </div>
          <div>
            <strong>Approved Amount</strong>
            <p>
              {fmtMoney(claim.approved_amount)}
              {claim.override_amount && <span className="badge badge-urgent" style={{ marginLeft: 6 }}>Override</span>}
            </p>
          </div>
          <div>
            <strong>Payment Method</strong>
            <p>{claim.payment_method === 'cheque' ? 'Cheque' : claim.payment_method === 'bank_transfer' ? 'Bank Transfer' : '—'}</p>
          </div>
          {claim.assessed_by && (
            <div>
              <strong>Assessed By</strong>
              <p>{claim.assessed_by.full_name}</p>
            </div>
          )}
          {claim.beneficiary && (
            <div>
              <strong>Beneficiary</strong>
              <p>{claim.beneficiary.full_name} ({labelFor(lists, 'beneficiary_relationship', claim.beneficiary.relationship)})</p>
            </div>
          )}
        </div>

        {claim.eligibility_flags && claim.eligibility_flags.length > 0 && (
          <div className="alert alert-error" style={{ marginTop: 12 }}>
            <strong>Eligibility flags (review, do not block):</strong>
            <ul>
              {claim.eligibility_flags.map((f, i) => (
                <li key={i}>{f.detail}</li>
              ))}
            </ul>
          </div>
        )}
        {claim.entitlement_calculation_note && (
          <p className="muted" style={{ marginTop: 12 }}>
            <strong>Basis of calculation:</strong> {claim.entitlement_calculation_note}
          </p>
        )}
        {claim.override_reason && (
          <p className="muted">
            <strong>Override reason:</strong> {claim.override_reason}
          </p>
        )}
        {claim.suspense_reason && (
          <p className="muted">
            <strong>Suspense reason:</strong> {claim.suspense_reason}
          </p>
        )}
        {claim.reversal_reason && (
          <p className="muted">
            <strong>Reversal reason:</strong> {claim.reversal_reason}
          </p>
        )}
        {claim.reissued_count > 0 && <p className="muted">Reissued {claim.reissued_count} time(s).</p>}
      </div>

      {claim.status === 'draft' && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h2>Supporting Documents</h2>
          <ul className="simple-list">
            {claim.documents.map((doc) => (
              <li key={doc.id}>
                {doc.original_name}
                {canCreate && (
                  <button type="button" className="btn btn-danger-ghost" onClick={() => removeDocument(doc.id)}>
                    Remove
                  </button>
                )}
              </li>
            ))}
            {claim.documents.length === 0 && <li className="muted">No documents attached yet.</li>}
          </ul>
          {canCreate && (
            <label className="btn btn-ghost" style={{ display: 'inline-block', cursor: 'pointer' }}>
              + Attach document
              <input type="file" accept="application/pdf,image/png,image/jpeg,.webp" onChange={uploadDocument} hidden />
            </label>
          )}
        </div>
      )}

      {claim.approval_levels_required && claim.approval_levels_required.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h2>Approval Chain</h2>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Level</th>
                  <th>Name</th>
                  <th>Decided By</th>
                  <th>Decision</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {claim.approval_levels_required.map((levelDef) => {
                  const approval = claim.approvals.find((a) => a.level === levelDef.level);
                  const isCurrent = claim.status === 'pending_approval' && claim.current_approval_level === levelDef.level;
                  return (
                    <tr key={levelDef.level}>
                      <td>{levelDef.level}</td>
                      <td>{levelDef.name}</td>
                      <td className="muted">{approval?.approver?.full_name ?? '—'}</td>
                      <td>
                        {approval ? (
                          <span className={`badge ${approval.decision === 'approved' ? 'badge-active' : 'badge-urgent'}`}>
                            {approval.decision}
                          </span>
                        ) : isCurrent ? (
                          <span className="badge badge-scheduled">Pending</span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="right">
                        {isCurrent && canActOnCurrentLevel && (
                          <button type="button" className="btn btn-ghost" onClick={() => openDecide(levelDef)}>
                            Decide
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {claim.active_batch_line && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h2>EFT Payment</h2>
          <p>
            Batch <strong>{claim.active_batch_line.batch.batch_number}</strong> —{' '}
            <span className="badge badge-neutral">{claim.active_batch_line.batch.status}</span>
          </p>
          {canDisburse && (
            <button type="button" className="btn btn-ghost" onClick={() => navigate(`/memberships/benefits/payment-batches/${claim.active_batch_line?.batch.id}`)}>
              View Batch
            </button>
          )}
        </div>
      )}

      {claim.active_cheque && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h2>Cheque</h2>
          <p>
            <strong>{claim.active_cheque.cheque_number}</strong> —{' '}
            <span className="badge badge-neutral">{claim.active_cheque.status}</span>
            {' '}({claim.active_cheque.signatures_collected}/{claim.active_cheque.required_signatures} signatures)
          </p>
          <button type="button" className="btn btn-ghost" onClick={() => navigate(`/memberships/benefits/cheques/${claim.active_cheque?.id}`)}>
            View Cheque
          </button>
        </div>
      )}

      {claim.reversals.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h2>Reversal Requests</h2>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Requested By</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th>Decided By</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {claim.reversals.map((r) => (
                  <tr key={r.id}>
                    <td>{r.requested_by?.full_name ?? '—'}</td>
                    <td className="muted">{r.reason}</td>
                    <td>
                      <span className={`badge ${r.status === 'approved' ? 'badge-active' : r.status === 'rejected' ? 'badge-urgent' : 'badge-neutral'}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="muted">{r.decided_by?.full_name ?? '—'}</td>
                    <td className="right">
                      {canDecideReversal && r.status === 'pending' && (
                        <button
                          type="button"
                          className="btn btn-ghost"
                          onClick={() => {
                            setReversalDecisionForm({ decision: 'approved', decision_comment: '' });
                            setDecidingReversal(r);
                          }}
                        >
                          Decide
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {decidingLevel && (
        <Modal title={`Decide: ${decidingLevel.name}`} onClose={() => setDecidingLevel(null)}>
          <form onSubmit={submitDecision} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Decision *
              <select
                value={decisionForm.decision}
                onChange={(e) => setDecisionForm({ ...decisionForm, decision: e.target.value as 'approved' | 'rejected' })}
              >
                <option value="approved">Approve</option>
                <option value="rejected">Reject</option>
              </select>
            </label>
            {decisionForm.decision === 'approved' && (
              <>
                <label className="span-2">
                  Approved amount
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={decisionForm.approved_amount}
                    onChange={(e) => setDecisionForm({ ...decisionForm, approved_amount: e.target.value })}
                  />
                  <small className="muted">
                    Defaults to the calculated entitlement. A higher amount is an override requiring senior approval.
                  </small>
                  {errors.approved_amount && <small className="error">{errors.approved_amount[0]}</small>}
                </label>
                <label className="span-2">
                  Override reason (only if approving above entitlement)
                  <textarea
                    rows={2}
                    value={decisionForm.override_reason}
                    onChange={(e) => setDecisionForm({ ...decisionForm, override_reason: e.target.value })}
                  />
                  {errors.override_reason && <small className="error">{errors.override_reason[0]}</small>}
                </label>
              </>
            )}
            <label className="span-2">
              Comment
              <textarea rows={2} value={decisionForm.comment} onChange={(e) => setDecisionForm({ ...decisionForm, comment: e.target.value })} />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setDecidingLevel(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}

      {requestingReversal && (
        <Modal title="Request reversal" onClose={() => setRequestingReversal(false)}>
          <form onSubmit={submitReversalRequest} className="form-grid">
            <label className="span-2">
              Reason *
              <textarea rows={3} value={reversalReason} onChange={(e) => setReversalReason(e.target.value)} required />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setRequestingReversal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary">Submit</button>
            </div>
          </form>
        </Modal>
      )}

      {decidingReversal && (
        <Modal title="Decide reversal request" onClose={() => setDecidingReversal(null)}>
          <form onSubmit={submitReversalDecision} className="form-grid">
            <p className="muted span-2">
              Requested by {decidingReversal.requested_by?.full_name ?? '—'}: {decidingReversal.reason}
            </p>
            <label className="span-2">
              Decision *
              <select
                value={reversalDecisionForm.decision}
                onChange={(e) => setReversalDecisionForm({ ...reversalDecisionForm, decision: e.target.value as 'approved' | 'rejected' })}
              >
                <option value="approved">Approve</option>
                <option value="rejected">Reject</option>
              </select>
            </label>
            <label className="span-2">
              Comment
              <textarea
                rows={2}
                value={reversalDecisionForm.decision_comment}
                onChange={(e) => setReversalDecisionForm({ ...reversalDecisionForm, decision_comment: e.target.value })}
              />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setDecidingReversal(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}

      {returningToSuspense && (
        <Modal title="Mark returned to suspense" onClose={() => setReturningToSuspense(false)}>
          <form onSubmit={submitReturnToSuspense} className="form-grid">
            <label className="span-2">
              Reason *
              <textarea rows={3} value={suspenseReason} onChange={(e) => setSuspenseReason(e.target.value)} required />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setReturningToSuspense(false)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
